import React, { useEffect, useState } from "react";
import { useTranslate } from "react-admin";

import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
} from "@mui/x-data-grid";
import EnumRiskLevel from "@wallet-manager/node-types/dist/types/postgres/const/RiskLevel";
import WalletRiskLevelRescanStatus from "@wallet-manager/node-types/dist/types/postgres/const/WalletRiskLevelRescanStatus";

import { Box } from "../MuiGenerals";
import { Button } from "../MuiGenerals";
import { NoRowsOverlay } from "../NoRowsOverlay";
import {
  dataGridDefaults,
  enumChainIdToType,
  RESCAN_LOCK_TIME_MS,
} from "../../utils/constant";
import {
  findChainInfo,
  sortObjectByKey,
  toDisplayTime,
} from "../../utils/helper";
import { useGenGridCol } from "../../utils/HelperComp";
import { useZusDialogStore } from "../../zustand/store";
import { listFace } from "../../views/Tools/AuditLogTabView";

import RescanRiskLevelDialog from "./RescanRiskLevelDialog";
import StatusBadge from "./StatusBadge";
import { CustomPagination } from "../CustomPagination";

interface propsFace {
  list: listFace;
  page: number;
  setPage: (page: number, details: any) => void;
  refresh: Function;
  // Server-supplied Merkle Science timeout (seconds, string). Used by the
  // Outcome cell to render the live lock countdown for Pending rows and by
  // the Apply cell to gate the disabled state. Falls back to
  // `RESCAN_LOCK_TIME_MS` if the field is missing or non-numeric.
  merkleSciApiTimeoutSec?: string;
}

// Format the remaining milliseconds as MM:SS. Shared by both the table's
// Outcome cell and the ApplyCell's disable check.
const formatRemaining = (ms: number): string => {
  if (ms <= 0) return "00:00";
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0",
  )}`;
};

export const getChainNameWithETHSeries = (
  chainType: number,
  chainId: number,
  walletType: number,
): string => {
  if (chainType === undefined || chainId === undefined) {
    return "";
  }

  const ETHChainType = Number(enumChainIdToType.ETH);
  const isETHChainType = chainType === ETHChainType;
  const isWalletType1 = walletType === 1;
  const isWalletType045 = [0, 4, 5].includes(walletType);

  if (isETHChainType && isWalletType1) {
    return "ALL ETH Series";
  }

  if (isETHChainType && isWalletType045) {
    return "Supported ETH Series";
  }

  return findChainInfo(String(chainType), String(chainId))?.name ?? "";
};

// Risk Checking Record tab. Each row comes from `getRiskCheckingRecords`
// with the snake_case wire shape (see `RiskCheckingRecord` in api/merchant.ts).
// We normalize the row into the camelCase keys the columns expect, and
// stash the original raw row in `_raw` so the action buttons can pass it
// straight to the dialogs without losing data.
const RiskCheckingRecordTabList = (props: propsFace) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`walletManagement.${key}`);
  // Used for the risk-level columns (Before / Returned).
  const tEnum = (key: string) => translate(`enumConstants.${key}`);
  const { list, page, setPage, refresh, merkleSciApiTimeoutSec } = props;
  const totalRecords = list.count;

  // Translate a risk-level number into the project's enum name. The backend
  // returns integers (e.g. `2` for Caution), so we look up the enum name
  // and translate. Empty / null stays blank so the cell collapses cleanly.
  const riskLevelText = (value: unknown): string => {
    if (value === null || value === undefined || value === "") return "";
    const numeric = Number(value);
    const enumName = EnumRiskLevel[numeric];
    return enumName !== undefined ? tEnum(enumName) : String(value);
  };

  // Resolve the server-supplied lock duration once at the table level so
  // every cell renders with the same value. The backend sends it as a
  // string ("300"); we convert to ms so the cell math stays consistent
  // with the dialog's lock check.
  const lockDurationMs =
    merkleSciApiTimeoutSec !== undefined
      ? Number(merkleSciApiTimeoutSec) * 1000
      : RESCAN_LOCK_TIME_MS;

  // Tick once per second so the Outcome countdown for Pending rows
  // refreshes. The whole table ticks (one timer per list, not per row)
  // — cheaper than per-row and keeps all rows visually in sync.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Map the raw snake_case row into the camelCase keys our column
  // definitions expect. `chain_name` is derived from `chain_type` /
  // `chain_id` via `findChainInfo` (the row itself doesn't carry it).
  // `rescan_time` is the `created_date` ISO string run through `toDisplayTime`
  // so the cell renders as "2026-09-25 09:08" rather than the raw timestamp.
  const content: GridRowsProp = list.rows.map((row: any, idx: number) => {
    const chainName = getChainNameWithETHSeries(
      row?.wallet?.chain_type,
      row?.wallet?.chain_id,
      row?.wallet?.wallet_type,
    );
    return {
      id: row?.id ?? idx,
      rescan_time: row?.created_date ? toDisplayTime(row.created_date) : "",
      wallet_address: row?.wallet_address ?? "",
      chain_name: chainName ?? "",
      // Nested `wallet.display_name` carries the human label. Fall back to
      // the wallet address when the wallet record isn't joined.
      display_name: row?.wallet?.display_name ?? row?.wallet_address ?? "",
      before: riskLevelText(row?.current_risk_level),
      returned: riskLevelText(row?.response_risk_level),
      outcome: row?.status ?? "",
      triggered_by: row?.created_by ?? "",
      // Stash the raw row so the action buttons can hand it to the dialogs
      // without losing fields.
      _raw: row,
    };
  });

  // Open the ProviderResponseDialog with the row's data in meta so it can
  // render the full `merkle_science_response`. The dialog reads from meta
  // for rescan_time, triggered_by, etc., and renders raw_response as JSON.
  const openProviderResponse = (rawRow: any) => {
    const chainName = getChainNameWithETHSeries(
      rawRow?.wallet?.chain_type,
      rawRow?.wallet?.chain_id,
      rawRow?.wallet?.wallet_type,
    );
    useZusDialogStore.getState().openExtra("providerResponseDialog", {
      wallet_address: rawRow?.wallet_address ?? "",
      chain_name: chainName ?? "",
      rescan_time: rawRow?.created_date ?? "",
      // ProviderResponseDialog reads `triggered_by` from meta (not
      // `rescan_triggered_by` like the apply step). Keep the meta key the
      // dialog actually reads so the field renders.
      triggered_by: rawRow?.created_by ?? "",
      current_risk_level: rawRow?.current_risk_level,
      returned_risk_level: rawRow?.response_risk_level,
      outcome: rawRow?.status !== undefined ? String(rawRow.status) : "",
      raw_response: rawRow?.merkle_science_response
        ? sortObjectByKey(rawRow.merkle_science_response)
        : null,
      error_reason: rawRow?.error_reason ?? null,
    });
  };

  // Risk Checking Record rows are historical — the rescan has already
  // happened, the result is in the record. So clicking Apply skips the
  // "confirm" step (which queries Merkle Science again) and lands directly
  // on the apply step. The dialog still honors a residual 5-minute lock if
  // the rescan happened moments ago — see RescanRiskLevelDialog.
  //
  // The dialog needs the row's full context (risk levels, addresses,
  // timestamps) so it can render the body. We pass every field the dialog
  // reads; everything else falls back to the row's cached values.
  const openApplyRescan = (rawRow: any) => {
    const chainName = getChainNameWithETHSeries(
      rawRow?.wallet?.chain_type,
      rawRow?.wallet?.chain_id,
      rawRow?.wallet?.wallet_type,
    );
    useZusDialogStore.getState().open("rescanRiskLevelDialog", {
      initialStep: "apply",
      // Identity — the apply call needs the record id.
      ...(rawRow?.id !== undefined && { rescan_id: Number(rawRow.id) }),
      // Display fields — read directly by the dialog subcomponents.
      display_name:
        rawRow?.wallet?.display_name ?? rawRow?.wallet_address ?? "",
      wallet_address: rawRow?.wallet_address ?? "",
      chain_name: chainName ?? "",
      chain_type: rawRow?.chain_type,
      chain_id: rawRow?.chain_id,
      current_risk_level: rawRow?.current_risk_level,
      returned_risk_level: rawRow?.response_risk_level,
      rescan_time: rawRow?.created_date ?? "",
      rescan_triggered_by: rawRow?.created_by ?? "",
      outcome: rawRow?.status !== undefined ? String(rawRow.status) : "",
      raw_response: rawRow?.merkle_science_response ?? null,
      error_reason: rawRow?.error_reason ?? null,
      triggered_by: rawRow?.created_by ?? "",
    });
  };

  const linkButtonSx = {
    textTransform: "none" as const,
    padding: 0,
    minWidth: 0,
    textDecoration: "underline",
  };

  const ViewResponseCell = (params: GridRenderCellParams) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <Button
        variant="text"
        color="secondary"
        onClick={() => openProviderResponse(params.row._raw)}
        sx={linkButtonSx}
      >
        {t("view_response")}
      </Button>
    </div>
  );

  // Apply is only enabled when:
  //   * the row's status is `Pending` (i.e. it hasn't been Applied/Failed/
  //     Expired yet — there's a real action to take), AND
  //   * the row's age is still within the lock window (`created_date` is
  //     less than `lockDurationMs` ago).
  // For any other status (Applied, Failed, Expired) the button is
  // disabled — the action would either be redundant or impossible.
  // When the timer hits 00:00 the button is also disabled: the row is
  // then considered stale and the user should rescan for a fresh result
  // before applying.
  const computeRowState = (
    rawRow: any,
  ): {
    isPending: boolean;
    remainingMs: number;
  } => {
    const isPending =
      Number(rawRow?.status) === WalletRiskLevelRescanStatus.Pending;
    const created = rawRow?.created_date
      ? new Date(rawRow.created_date).getTime()
      : 0;
    const remainingMs = created
      ? Math.max(0, lockDurationMs - (now - created))
      : 0;
    return { isPending, remainingMs };
  };

  const isRowApplyable = (rawRow: any): boolean => {
    if (!rawRow) return false;
    const { isPending, remainingMs } = computeRowState(rawRow);
    return isPending && remainingMs > 0;
  };

  const ApplyCell = (params: GridRenderCellParams) => {
    const rawRow = params.row._raw;
    const applyable = isRowApplyable(rawRow);
    const { isPending, remainingMs } = computeRowState(rawRow);
    // Show the live countdown on the button label while it's running.
    // When the timer hits 00:00, drop the suffix so the button reads just
    // "Apply" (it's also disabled at that point — see `isRowApplyable`).
    const buttonLabel =
      isPending && remainingMs > 0
        ? `${t("apply")} (${formatRemaining(remainingMs)})`
        : t("apply");
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          onClick={() => openApplyRescan(params.row._raw)}
          disabled={!applyable}
          sx={{
            textTransform: "none",
            minWidth: 0,
          }}
        >
          {buttonLabel}
        </Button>
      </div>
    );
  };

  // The Outcome cell renders the translated status badge with the matching
  // status color. The live countdown used to live here too; it now lives on
  // the Apply button label so the user sees the deadline right where they'd
  // take the action.
  const OutcomeCell = (params: GridRenderCellParams) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <StatusBadge status={params.value} />
    </Box>
  );

  const columns: GridColDef[] = [
    useGenGridCol("rescan_time", t("rescan_time"), { minWidth: 170 }),
    useGenGridCol("wallet_address", t("walletAddress"), { minWidth: 320 }),
    useGenGridCol("chain_name", t("chain_name"), { minWidth: 150 }),
    useGenGridCol("display_name", t("display_name"), { minWidth: 160 }),
    useGenGridCol("before", t("before"), { minWidth: 120 }),
    useGenGridCol("returned", t("returned"), { minWidth: 120 }),
    {
      field: "outcome",
      headerName: t("outcome"),
      flex: 1,
      minWidth: 130,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: OutcomeCell,
    },
    useGenGridCol("triggered_by", t("triggered_by"), { minWidth: 180 }),
    {
      field: "details",
      headerName: t("details"),
      flex: 1,
      minWidth: 140,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: ViewResponseCell,
    },
    {
      field: "operation",
      headerName: t("operation"),
      flex: 1,
      minWidth: 140,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: ApplyCell,
    },
  ];

  return (
    <>
      <DataGrid
        {...dataGridDefaults}
        rows={content}
        columns={columns}
        rowCount={totalRecords}
        page={page}
        onPageChange={setPage}
        components={{
          // Use the project's standard empty-state overlay — same as every
          // other table in the app. It renders the localized
          // `component.noRecords` message ("No rows").
          NoRowsOverlay,
          Footer: CustomPagination,
        }}
        componentsProps={{
          footer: { totalRecords },
        }}
        disableColumnMenu
      />
      <RescanRiskLevelDialog refresh={() => refresh()} />
    </>
  );
};

export default React.memo(RiskCheckingRecordTabList);
